const BETA_METADATA_KEYS = new Set([
    'activeTextEditor',
    'activeTextSelectionState',
    'editingElementId',
    'elementForEdit',
    'importedSectionPreviews',
    'previewDevice',
    'previewSurface',
    'schema',
    'sectionForEdit',
    'sectionIndex',
    'selectedElementId',
    'selectedElementType',
    'selectedSectionId',
    'selectedSectionIndex',
    'sidebarReference',
])

const PERSISTABLE_FRAME_KEYS = [
    'action',
    'actionType',
    'backgroundImage',
    'component',
    'coverImage',
    'extra',
    'frameContent',
    'frameDataId',
    'frameId',
    'frameOrigin',
    'frameStatus',
    'frameType',
    'grid',
    'gridMobile',
    'height',
    'heigth',
    'mainImage',
    'new',
    'newBefore',
    'options',
    'subPagesCategories',
    'subpagesCategories',
    'temporaryId',
    'text',
    'width',
]

const FRAME_TYPES_WITH_CHILDREN = new Set([
    'comments',
    'container',
    'post',
    'row',
])

export const TITLE_FRAME_CONTENT_MAX_BYTES = 255

const hasOwn = (value: any, key: string) => Object.prototype.hasOwnProperty.call(value, key)

const cloneValue = <T>(value: T): T => {
    if (value === undefined || value === null) return value

    return JSON.parse(JSON.stringify(value))
}

const isObjectRecord = (value: any) => (
    value !== null
    && typeof value === 'object'
    && !Array.isArray(value)
)

const isBetaMetadataKey = (key: string) => (
    BETA_METADATA_KEYS.has(key)
    || key.startsWith('activeText')
    || key.startsWith('editing')
    || key.startsWith('selected')
)

export const truncateUtf8Bytes = (value: string, maxBytes: number) => {
    const encoder = new TextEncoder()

    if (encoder.encode(value).length <= maxBytes) return value

    let bytes = 0
    let result = ''

    for (const char of value) {
        const charBytes = encoder.encode(char).length

        if (bytes + charBytes > maxBytes) break

        result += char
        bytes += charBytes
    }

    return result
}

export const normalizeTitleFrameContent = (value: any) => (
    typeof value === 'string'
        ? truncateUtf8Bytes(value, TITLE_FRAME_CONTENT_MAX_BYTES)
        : value
)

export const normalizeEditorFrameType = (frameType?: string | null): string => {
    const normalizedFrameType = String(frameType ?? '').toLowerCase()

    switch (normalizedFrameType) {
        case 'beforeafterimage':
        case 'before-after-image':
        case 'before_after_image':
            return 'beforeAfterImage'
        default:
            return frameType ?? ''
    }
}

export const stripBetaEditorMetadata = (value: any): any => {
    if (Array.isArray(value)) {
        return value.map(stripBetaEditorMetadata)
    }

    if (!isObjectRecord(value)) {
        return value
    }

    return Object.entries(value).reduce((result, [key, entryValue]) => {
        if (isBetaMetadataKey(key)) return result

        result[key] = stripBetaEditorMetadata(entryValue)

        return result
    }, {} as Record<string, any>)
}

const sanitizeFrameContent = (frameType: any, frameContent: any) => {
    const normalizedFrameType = normalizeEditorFrameType(frameType)

    if (Array.isArray(frameContent) && FRAME_TYPES_WITH_CHILDREN.has(String(normalizedFrameType))) {
        return frameContent.map(sanitizeEditorFrameForPayload).filter(Boolean)
    }

    if (normalizedFrameType === 'title') {
        return stripBetaEditorMetadata(normalizeTitleFrameContent(frameContent))
    }

    return stripBetaEditorMetadata(frameContent)
}

export const sanitizeEditorFrameForPayload = (frame: any): any => {
    if (!isObjectRecord(frame)) return frame

    return PERSISTABLE_FRAME_KEYS.reduce((result, key) => {
        if (!hasOwn(frame, key)) return result

        if (key === 'frameType') {
            result.frameType = normalizeEditorFrameType(frame.frameType)
            return result
        }

        if (key === 'frameContent') {
            result.frameContent = sanitizeFrameContent(frame.frameType, frame.frameContent)
            return result
        }

        result[key] = stripBetaEditorMetadata(frame[key])

        return result
    }, {} as Record<string, any>)
}

const sanitizeDeletedFrames = (items: any[] | null | undefined) => {
    if (!Array.isArray(items)) return []

    return items.map(sanitizeEditorFrameForPayload).filter(Boolean)
}

export const createBetaEditorPublishPayload = (
    activePage: any,
    deletedFrames?: any[] | null,
    deletedSections?: any[] | null,
) => {
    const payload = stripBetaEditorMetadata(cloneValue(activePage ?? {}))

    payload.content = Array.isArray(activePage?.content)
        ? activePage.content.map(sanitizeEditorFrameForPayload).filter(Boolean)
        : []

    payload.deletedFrames = sanitizeDeletedFrames(deletedFrames)

    const sanitizedDeletedSections = sanitizeDeletedFrames(deletedSections)

    if (sanitizedDeletedSections.length > 0) {
        payload.deletedSections = sanitizedDeletedSections
    } else {
        delete payload.deletedSections
    }

    return payload
}
